import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

export type SocketState = 'unknown' | 'connected' | 'disconnected';

export interface Connectivity {
  /** `unknown` until the first connect or connect error */
  readonly socket: SocketState;
  /** the backend is in a maintenance window and refuses this user */
  readonly maintenance: boolean;
}

/**
 * Whether the app can talk to its backend right now — one answer for the whole app.
 *
 * The answer is derived from two facts that come from two different places: the socket is
 * connected (reported by the `WebSocketClient`) and the backend is not in a maintenance window
 * for this user (reported by the `ShutdownBroker`). Each producer writes the one field it knows
 * about; nobody else writes. `isOnline` is true only when both facts hold.
 *
 * The value replays, so a component created long after the socket connected gets the current
 * answer, and it is available synchronously for code that has to decide inside a method.
 * Repeated writes of the same value are dropped, so a reconnect loop is one transition, not one
 * per attempt. Every transition is written to the console; Sentry's console integration keeps it
 * as a breadcrumb.
 *
 * Consumers:
 * - `SessionController`: `isOnline$` to re-authenticate the socket when it comes back and to take
 *   the session offline when it goes; `isSocketConnected` to decide how a logout reaches the
 *   backend and the identity provider.
 * - Data services and pipeline elements: `isOnline` before a server call; the
 *   `MaintenanceSynchroniser` uses `isSocketConnected` because it must sync during maintenance.
 * - UI: `isOnline$` for online/offline hints, `state$` where maintenance and a lost socket are
 *   shown differently (session banner).
 * - Diagnostics: `isOnline` as a tag on logs and tracking events.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionStateService {
  private readonly _state$ = new BehaviorSubject<Connectivity>({ socket: 'unknown', maintenance: false });

  /** Both facts, replaying the current value. */
  public readonly state$: Observable<Connectivity> = this._state$.asObservable();

  /** Online/offline transitions, replaying — but silent until the socket state is known. */
  public readonly isOnline$: Observable<boolean> = this._state$.pipe(
    filter(state => state.socket !== 'unknown'),
    map(isOnline),
    distinctUntilChanged()
  );

  /** Both facts as they are right now. */
  public get state(): Connectivity {
    return this._state$.value;
  }

  /** Socket connected and no maintenance: a request can succeed. */
  public get isOnline(): boolean {
    return isOnline(this._state$.value);
  }

  /** The socket alone, regardless of maintenance. */
  public get isSocketConnected(): boolean {
    return this._state$.value.socket === 'connected';
  }

  /** Written by the WebSocketClient on every socket event. */
  public setSocket(socket: SocketState, reason?: string): void {
    this.update({ socket }, reason ?? `socket ${socket}`);
  }

  /** Written by the ShutdownBroker when a maintenance window starts or ends for this user. */
  public setMaintenance(maintenance: boolean): void {
    this.update({ maintenance }, maintenance ? 'maintenance started' : 'maintenance ended');
  }

  private update(patch: Partial<Connectivity>, reason: string): void {
    const previous = this._state$.value;
    const next: Connectivity = { ...previous, ...patch };
    if (next.socket === previous.socket && next.maintenance === previous.maintenance) {
      return;
    }
    this._state$.next(next);
    logTransition(previous, next, reason);
  }
}

function isOnline(state: Connectivity): boolean {
  return state.socket === 'connected' && !state.maintenance;
}

function logTransition(previous: Connectivity, next: Connectivity, reason: string): void {
  const message =
    `[connectivity] online ${isOnline(previous)} -> ${isOnline(next)} | ` +
    `socket=${next.socket} maintenance=${next.maintenance} | ${reason}`;
  console.info(message);
}
